import React, { useState, useEffect, useCallback } from 'react';
import { BsArrowRight, BsArrowLeft } from 'react-icons/bs';
import ReactLoader from 'react-loading';
import { useSelector } from 'react-redux';

import * as Sentry from '@sentry/react';
import { RootState } from 'store';
import { ColorPalette } from 'theme/colors';
import { TokenManager, TokenizationProviders, TokenManagerEvents } from 'token-manager-web';

import { Box } from 'components/Buttons/Button/styles';
import Loader from 'components/Loader';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { addPaymentCard } from 'services/paymentMethodService';
import { htmlT } from 'utils/generic';
import { cardTypeNormalize } from 'utils/payment';

import CreditCardImage from './components/CreditCardImage';
import {
  Container,
  Content,
  FieldContainer,
  Field,
  Label,
  Error,
  ButtonContainer,
  ButtonNavContainer,
  Button,
  WarningContainer,
  StepContainer,
  LoaderContainer,
} from './styles';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT as string;
const isProduction = !ENVIRONMENT.startsWith('DEV') && ENVIRONMENT !== 'LOCAL';

const LoaderButton = ({ fontColor }: { fontColor: string }) => (
  <Box>
    <ReactLoader type="spinningBubbles" color={fontColor} width={30} height={30} />
  </Box>
);

interface Props {
  tokenizer?: string;
  flag?: string;
  groupId?: string;
  onAdded: (cardId?: number) => void;
}

type InputChangeField = {
  value: string;
  error: string;
  isFocused: boolean;
  isValid: boolean;
};

type InputChangeData = {
  card_number: InputChangeField;
  card_cvv: InputChangeField;
  expiration_date: InputChangeField;
  first_name: InputChangeField;
  last_name: InputChangeField;
};

type CardTypeData = {
  cardType: string;
};

enum ErrorTypes {
  IS_REQUIRED = 'IS_REQUIRED',
  INVALID_EXPIRATION_DATE = 'INVALID_EXPIRATION_DATE',
  INVALID_CARD_NUMBER = 'INVALID_CARD_NUMBER',
  INVALID_CVV = 'INVALID_CVV',
  INVALID_VALUE = 'INVALID_VALUE',
  EMPTY = '',
}

const adjustInputElements = (id: string) => {
  const all = document.querySelectorAll(`[id=${id}]`);
  const first = all[0];

  // Removes duplicates with the same ID
  if (all.length > 1) {
    Array.from(all)
      .slice(1)
      .forEach((element) => {
        element.remove();
      });
  }

  // Removes browser autocomplete
  if (first) {
    (first as HTMLInputElement).autocomplete = 'off';
  }
};

const CreditCard = ({ tokenizer, flag, groupId, onAdded }: Props) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const { t } = useStoreTranslation();
  const userResult = useSelector((state: RootState) => state.user.userResult);
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const [step, setStep] = useState(0);
  const [tokenManager, setTokenManager] = useState<TokenManager | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [response, setResponse] = useState<any>(null);
  const [cardType, setCardType] = useState('');

  const [cardNumberError, setCardNumberError] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [expirationError, setExpirationError] = useState('');

  const [cardNumberText, setCardNumberText] = useState('');
  const [cvvText, setCvvText] = useState('');
  const [holderNameText, setHolderNameText] = useState('');
  const [expiryText, setExpiryText] = useState('');

  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cvvValid, setCvvValid] = useState(false);
  const [holderNameValid, setHolderNameValid] = useState(false);
  const [expiryValid, setExpiryValid] = useState(false);

  const [nextDisabled, setNextDisabled] = useState(false);

  const handleNext = () => {
    setStep(step + 1);
    setNextDisabled(true);
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleRegister = useCallback(async () => {
    if (!tokenManager) return;

    setIsSubmitting(true);
    setDisabled(true);

    try {
      const token = localStorage.getItem('token');

      const result = await tokenManager.tokenizeCreditCard({
        countryCode: accountResult !== null ? accountResult.countryId : 'CO',
        userToken: token ?? '',
      });
      setDisabled(false);
      setResponse(result);
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Could not tokenize card',
        data: { error, message: error.message },
      });
      Sentry.captureMessage('[CreditCard] Could not tokenize card');

      setIsSubmitting(false);
      const message =
        error.errors && error.errors.length > 0 ? error.errors[0].message : 'Error while saving card information';
      toast.add({
        type: 'danger',
        message: message,
      });
    }
  }, [accountResult, userResult, toast, tokenManager]);

  const clearFields = () => {
    setCardNumberText('');
    setCvvText('');
    setHolderNameText('');
    setExpiryText('');
    setCardNumberValid(false);
    setCvvValid(false);
    setHolderNameValid(false);
    setExpiryValid(false);
  };

  useEffect(() => {
    setStep(1);
    setIsSubmitting(false);

    clearFields();
  }, []);

  const handleAddPaymentCard = useCallback(async () => {
    if (response && accountResult !== null) {
      try {
        const { data } = await addPaymentCard(
          response,
          accountResult && typeof accountResult.emailRappi !== 'undefined' ? accountResult?.emailRappi : '',
          accountResult?.documentNumber,
          groupId,
        );
        setIsSubmitting(false);
        onAdded(data.id);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Could not add card',
          data: { error, message: error.message },
        });
        Sentry.captureMessage('[CreditCard] Could not add card');

        toast.add({
          type: 'danger',
          message: t('payment.cardForm.errors.generic'),
        });

        setIsSubmitting(false);
      }
    }
  }, [response, accountResult, groupId, onAdded, toast, t]);

  useEffect(() => {
    handleAddPaymentCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, accountResult]);

  const messageError = useCallback(
    (message: string) => {
      const ERRORS = [
        {
          id: ErrorTypes.IS_REQUIRED,
          message: t('payment.errors.IS_REQUIRED'),
        },
        {
          id: ErrorTypes.INVALID_EXPIRATION_DATE,
          message: t('payment.errors.INVALID_EXPIRATION_DATE'),
        },
        {
          id: ErrorTypes.INVALID_CARD_NUMBER,
          message: t('payment.errors.INVALID_CARD_NUMBER'),
        },
        {
          id: ErrorTypes.INVALID_CVV,
          message: t('payment.errors.INVALID_CVV'),
        },
        {
          id: ErrorTypes.INVALID_VALUE,
          message: t('payment.errors.INVALID_VALUE'),
        },
      ];

      const result = ERRORS.filter((e) => e.id === message);
      if (result.length > 0) {
        return result[0].message;
      }
      return message;
    },
    [t],
  );

  useEffect(() => {
    const manager = new TokenManager({
      email: accountResult !== null && typeof accountResult.emailRappi !== 'undefined' ? accountResult.emailRappi : '',
      isProduction,
      provider: tokenizer === 'VGS' ? TokenizationProviders.VGS : TokenizationProviders.SPREEDLY,
    });
    setTokenManager(manager);

    manager.addEventListener(TokenManagerEvents.CARD_TYPE, (data: CardTypeData) => {
      let { cardType } = data;
      cardType = cardTypeNormalize(cardType);
      setCardType(cardType);
    });

    manager.addEventListener(TokenManagerEvents.INPUT_CHANGE, (data: InputChangeData) => {
      clearFields();
      setCardNumberError(messageError(data.card_number.error));
      setCvvError(messageError(data.card_cvv.error));
      setFirstNameError(messageError(data.first_name.error));
      setLastNameError(messageError(data.last_name.error));
      setExpirationError(messageError(data.expiration_date.error));

      setCardNumberValid(data.card_number.isValid);
      setCvvValid(data.card_cvv.isValid);
      setHolderNameValid(data.first_name.isValid && data.last_name.isValid);
      setExpiryValid(data.expiration_date.isValid);

      if (data.card_number.isValid) setCardNumberText('•••• •••• •••• ••••');
      if (data.card_cvv.isValid) setCvvText('• • •');
      const first_name = data.first_name.isValid ? data.first_name.value : '';
      const last_name = data.last_name.isValid ? data.last_name.value : '';
      setHolderNameText(`${first_name} ${last_name}`);
      if (data.expiration_date.isValid) setExpiryText(data.expiration_date.value);
    });

    manager.addEventListener(TokenManagerEvents.READY, () => {
      if (window.Spreedly && window.Spreedly.isLoaded()) {
        window.Spreedly.on('ready', () => {
          const style = `width: 100%; height: 100%; padding: 5px 0; font-size: 16px; line-height: 1.5em; color: rgb(${ColorPalette.RGB.CharlestonGreen});`;
          window.Spreedly?.setStyle('number', style);
          window.Spreedly?.setStyle('cvv', style);

          adjustInputElements('first-name-field');
          adjustInputElements('last-name-field');
          adjustInputElements('expiration-date-field');

          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    manager.addEventListener(TokenManagerEvents.LOAD_ERROR, (error: string) => {
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Could initialize token-manager-web',
        data: { error },
      });
      Sentry.captureMessage('[CreditCard] Could initialize token-manager-web');

      toast.add({
        type: 'danger',
        message: error,
      });
      setIsLoading(false);
    });

    manager
      .withCardNumber({
        elementId: 'card-number',
        placeholder: t('payment.cardForm.placeholder.cardNumber'),
      })
      .withCVV({
        elementId: 'cvv',
        placeholder: t('payment.cardForm.placeholder.cvv'),
      })
      .withLastName({
        elementId: 'last-name',
        placeholder: t('payment.cardForm.placeholder.lastName'),
      })
      .withFirstName({
        elementId: 'first-name',
        placeholder: t('payment.cardForm.placeholder.firstName'),
      })
      .withExpirationDate({
        elementId: 'expiration-date',
        placeholder: t('payment.cardForm.placeholder.expirationDate'),
      })
      .build();

    return () => {
      manager.removeHandlers();
    };
  }, [t, tokenizer, messageError, toast, accountResult]);

  useEffect(() => {
    switch (step) {
      case 1:
        setNextDisabled(!cardNumberValid);
        break;
      case 2:
        setNextDisabled(!holderNameValid);
        break;
      case 3:
        setNextDisabled(!expiryValid);
        break;
      case 4:
        setNextDisabled(!cvvValid);
        setDisabled(!cvvValid);
        break;
      default:
        setNextDisabled(false);
    }
  }, [step, cardNumberValid, holderNameValid, expiryValid, cvvValid]);

  return (
    <Container className={`step-${step}`}>
      {isLoading && (
        <LoaderContainer>
          <Loader fullScreen={isMobile} />
        </LoaderContainer>
      )}
      <CreditCardImage
        isFlipped={step > 3}
        cardType={cardType === '' ? (typeof flag !== 'undefined' ? flag : '') : cardType}
        cardNumber={cardNumberText}
        holderName={holderNameText}
        expiryDate={expiryText}
        cvv={cvvText}
      />

      <Content>
        <StepContainer>
          <FieldContainer className={cardNumberError ? 'field-card-number field-error' : 'field-card-number'}>
            <Label htmlFor="card-number">{t('payment.cardForm.cardNumber')}</Label>
            <Field id="card-number" />
            {cardNumberError && <Error>{cardNumberError}</Error>}
          </FieldContainer>
        </StepContainer>
        <StepContainer>
          <FieldContainer className={cvvError ? 'field-cvv field-error' : 'field-cvv'}>
            <Label htmlFor="cvv">{t('payment.cardForm.cvv')}</Label>
            <Field id="cvv" />
            {cvvError && <Error>{cvvError}</Error>}
          </FieldContainer>
        </StepContainer>
        <StepContainer>
          <FieldContainer className={firstNameError ? 'field-first-name field-error' : 'field-first-name'}>
            <Label htmlFor="first-name">{t('payment.cardForm.firstName')}</Label>
            <Field id="first-name" />
            {firstNameError && <Error>{firstNameError}</Error>}
          </FieldContainer>
          <FieldContainer className={lastNameError ? 'field-last-name field-error' : 'field-last-name'}>
            <Label htmlFor="last-name">{t('payment.cardForm.lastName')}</Label>
            <Field id="last-name" />
            {lastNameError && <Error>{lastNameError}</Error>}
          </FieldContainer>
        </StepContainer>
        <StepContainer>
          <FieldContainer className={expirationError ? 'field-expiration-date field-error' : 'field-expiration-date'}>
            <Label htmlFor="date">{t('payment.cardForm.expirationDate')}</Label>
            <Field id="expiration-date" />
            {expirationError && <Error>{expirationError}</Error>}
          </FieldContainer>
        </StepContainer>
        <WarningContainer>
          <img src="/images/payment/addcard-warning.svg" alt="" />
          <span>{htmlT(t, 'payment.cardForm.warning')}</span>
        </WarningContainer>
        <ButtonNavContainer>
          <Button className="btn-prev" onClick={handlePrev}>
            <BsArrowLeft />
          </Button>
          <Button disabled={nextDisabled} className="btn-next" onClick={handleNext}>
            <BsArrowRight />
          </Button>
        </ButtonNavContainer>
        <ButtonContainer>
          <Button
            className="btn-register-cc"
            onClick={handleRegister}
            disabled={(disabled && nextDisabled) || isSubmitting}
          >
            {isSubmitting ? <LoaderButton fontColor={ColorPalette.HEX.White} /> : t('payment.cardForm.btnSave')}
          </Button>
        </ButtonContainer>
      </Content>
    </Container>
  );
};

export default CreditCard;
